// Sugalarm — single owner of alarm-audio lifecycle.
//
// Cycle 20.x reorganization: the `low` / `urgent_low` / `urgent_high`
// loops are now driven by the `useAlarmEpisode` state machine instead
// of self-firing off `socket.lastAlert`. The episode tracks phase
// (active / snoozed / silenced) and we mirror its `audioOn` flag onto
// the right audio element. Outside that flow:
//
//   - `high` (non-urgent) is still a one-shot chime tied to a new
//     alert id (no episode flow per user spec).
//   - Low / urgent-low **predictions** still play `predictionAlert.mp3`
//     exactly twice. Predictions are not part of the episode state.
//
// `enabled` (settings.alerts_enabled) silences every path. Cycle 216:
// quiet hours were removed platform-wide — there is no longer any
// time-of-day suppression anywhere. An alert frame that reaches us is
// one the engine decided should sound, full stop.
//
// Test-mode imperative methods are exposed for `TestAlarmDriver` to
// drive the same audio elements; this keeps every alarm sound in one
// owner so test mode can't desynchronise from a real fire.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CUE_EVENT_AGE_MS,
  cueEventFresh,
  mayEmitHighCadenceCue,
} from "../lib/alarmCue";
import { audibleAlarmLevel } from "../lib/alarmAudible";
import { isStale, zoneOf } from "../lib/glucose";
import type { Thresholds, Zone } from "../lib/types";
import {
  type AlarmZone,
  ALARM_SOUND_DEFAULT_FILE,
  resolveAlarmSoundFile,
} from "../lib/alarmSoundOptions";
import type { AlarmEpisodeState, EpisodePhase } from "./useAlarmEpisode";
import type { GlucoseSocketState } from "./useGlucoseSocket";
import { useNow } from "./useNow";
import {
  noDataCueDue,
  noDataOutageKey,
  noDataSilenced,
  withNoDataSilence,
  type NoDataSilences,
} from "../lib/noDataAlarm";

export interface AlarmSounds {
  /** One-shot — used by TestAlarmDriver only. The real `low` flow loops
   *  via the episode state. */
  playLow: () => void;
  playHigh: () => void;
  /** Looping — used by TestAlarmDriver only. Real low/urgent_low flow
   *  drives the loop via the episode. */
  startUrgentLowLoop: () => void;
  /** Cycle 374: play the urgent-low cue ONCE. This is what a real fire
   *  does on every non-kiosk surface now, so the Settings preview uses it
   *  too — the same reasoning cycle 232 applied to urgent_high when its
   *  loop went away: an audition that loops a sound the alarm no longer
   *  loops is a preview of something that does not exist. */
  playUrgentLowOnce: () => void;
  startUrgentHighLoop: () => void;
  startLowLoop: () => void;
  stopAllLoops: () => void;
  playPredictionLowTwice: () => void;
  /** Cycle 33: return-to-range chime. Fires automatically when a
   *  reading bridges from `low`/`urgent_low`/`high`/`urgent_high` back
   *  into `in_range`. Exposed imperatively so TestAlarmDriver can step
   *  through it as part of the cycle. */
  playNormal: () => void;
  /** Cycle 33: stale-stream chime fired immediately on stale-true
   *  transition and every 10 min thereafter while staleness persists.
   *  Exposed imperatively for test mode. */
  playNoData: () => void;
  /** Cycle 407: one short cue (the treating tick, reduced gain) when a
   *  check-in question ARRIVES, so a kiosk with no speech path still
   *  announces that it is asking. Kiosk-only, like the tick; a no-op
   *  elsewhere. */
  playTreatingTick: () => void;
  /** Cycle 243.5: play the reminder chime once, then invoke `onEnded` when
   *  it finishes (so the caller can speak the reminder TTS after it). Not
   *  gated by `alerts_enabled` — reminders are independent of CGM alerts. */
  playReminderChime: (onEnded?: () => void) => void;
  loopActive: boolean;
  /** Cycle 451.1: the level this bridge is driving RIGHT NOW, or null — the
   *  output of the one shared `audibleAlarmLevel` derivation. Exposed so the
   *  return-to-Now route rule can read what actually sounds instead of
   *  re-deriving it from episode fields. Read-only; nothing about the audio
   *  depends on who looks at it. */
  audibleAlarmLevel: "low" | "urgent_low" | null;
  /** Cycle 451.1: this surface gives urgent low the kiosk REPEATING cue
   *  rather than the one-shot (`urgentLowPlaybackMode`). Mirrors the opt so
   *  the route rule can tell sustained audio from a one-shot without
   *  re-deriving the surface. */
  kioskUrgentRepeat: boolean;
  /** Cycle 451.1: increments each time the off-kiosk urgent-low ONE-SHOT
   *  is DRIVEN — the cue, not the level.
   *
   *  ⚠ IT COUNTS A CUE THE BRIDGE DECIDED TO PLAY, NOT A CONFIRMED SOUND.
   *  It is bumped inside the driver's own `playUrgentLowOnce()` branch, so
   *  cycle 381's `inAppCueQuiet` skip (a voice call is in flight, the cue is
   *  suppressed but the level is still marked driven) does NOT bump it, and
   *  the preview — which calls `playUrgentLowOnce` directly, never through
   *  the driver — cannot bump it either. But `playUrgentLowOnce` plays
   *  through `safePlay`, which swallows a rejected `play()`, so an
   *  autoplay-BLOCKED cue still bumps. That is the deliberate direction: a
   *  surface whose audio the browser refused is exactly where the caregiver
   *  most needs the control put in front of them, and the codebase already
   *  fails open toward sound elsewhere (`inAppCueQuiet`). The cost of the
   *  other choice is silence AND no control. */
  urgentCueSeq: number;
  // Cycle 242: preview a zone's currently-selected sound (Settings picker).
  // Plays once through a dedicated owner-held element — generates NO db
  // alert and opens NO episode. No-op while a real urgent episode is active
  // (a real alarm always wins). `previewingZone` is the zone currently
  // previewing (for the stop affordance), or null.
  previewSound: (zone: AlarmZone, id: string) => void;
  stopPreview: () => void;
  previewingZone: AlarmZone | null;
  // Cycle 360.5: true for the whole time the kiosk in-app no-data alert is
  // raised — drives the frame-level Silence banner. Always false on non-kiosk
  // surfaces (phones get the cycle-360 critical push instead). Cycle 386: the
  // TONE is a one-shot fired on this flag's rising edge, so this stays true
  // long after the audio ends — it tracks the OUTAGE, not the sound.
  noDataAlarmActive: boolean;
  // Cycle 360.5: clear the CURRENT no-data outage. Outage-keyed — a later
  // outage always re-alarms and data returning auto-clears it, so the no-data
  // alert can never be permanently muted.
  silenceNoData: () => void;
}

export interface UseAlarmSoundsOpts {
  enabled: boolean;
  socket: GlucoseSocketState;
  activeMemberId: string | null;
  /** Episode state machine — when supplied, drives low / urgent_low /
   *  urgent_high audio. Optional so TestAlarmDriver can keep using
   *  the imperative path without setting up a real episode. */
  episode?: AlarmEpisodeState;
  /** Cycle 35: live thresholds from `useThresholds()`. Drives the
   *  return-to-range chime so the bridge resolves zones against the
   *  user's configured boundaries, not the 70/180 defaults. */
  thresholds: Thresholds;
  /** Cycle 242: the five per-zone custom-sound setting ids (from
   *  `useSettings()`). Each "default" or a curated skin id; absent →
   *  "default". Drives the per-element `src`; an unknown id resolves to
   *  the zone's default file via the manifest. */
  soundSelection?: Partial<Record<AlarmZone, string>>;
  /** Cycle 360.5: enable the in-app no-data alert. KIOSK ONLY — the
   *  always-foregrounded tablet, which can't rely on push for a dark-feed
   *  alert (Android FCM plays a generic channel sound). Pass
   *  `variant === "kiosk"`; omitted/false everywhere else.
   *
   *  Named `...LoopEnabled` from cycle 360.5, when the tone looped. Cycle 386
   *  made it a ONE-SHOT; the flag name is kept because it is threaded through
   *  `App.tsx` and renaming it buys nothing but churn on an alarm-path file. */
  noDataLoopEnabled?: boolean;
  /** Cycle 468: the active member's monitoring is suspended by the
   *  subscription lock (the server's `service_state`). A locked member is
   *  not monitored at all, so a feed that goes quiet after the lock is the
   *  lock, not an outage: the in-app No Data alarm stands down. Read from
   *  the socket's per-member map at the call site; never derived here. */
  serviceLocked?: boolean;
  /** Cycle 360.5: a bounded sensor-pause hold (cycle 354) is active for the
   *  active member — the no-data loop stands down (a planned outage: sensor
   *  change / away). Derived from `settings.sensor_pause.until` at the call
   *  site so the same control silences the push page AND this loop. */
  sensorPauseActive?: boolean;
  /** Cycle 374: the urgent-low cue REPEATS on an escalating interval
   *  instead of playing once. KIOSK ONLY — pass `variant === "kiosk"`.
   *
   *  The kiosk is the single surface guaranteed to be in the room and the
   *  single surface with no push and no phone call behind it, so it is
   *  where the never-silent floor has to live once the continuous loop is
   *  gone. Omitted/false everywhere else → play once per fire. */
  kioskUrgentRepeat?: boolean;
  /** Cycle 391: a treating window is OPEN on this episode.
   *
   *  It does NOT gate the alarm loop — `episode.audioOn` already factors
   *  `!treating` in, so the existing bridge stops (and restarts) through
   *  the one path it always used. What this drives is the KIOSK SOFT
   *  TICK: while a window is open the kiosk plays one short cue every
   *  five minutes at reduced gain, so the room can never conclude the
   *  system is off. Phones and the web SPA go quiet for the window —
   *  the kiosk is the surface guaranteed to be in the room, which is the
   *  same reasoning cycle 374 used for the repeating cue itself. */
  treatingActive?: boolean;
  /** Cycle 381: absolute epoch-ms deadline from the server's
   *  `voice_call_placed` frame. While `Date.now()` is before it, this
   *  PHONE skips the redundant urgent-low one-shot rather than play a
   *  second identical sound over the ringing call. Null/absent/past ⇒ no
   *  suppression, which is the fail-open direction. */
  voiceCallQuietUntil?: number | null;
  /** Cycle 403: absolute epoch-ms deadline from the server's `call_quiet`
   *  frame for THIS session's own account — we are on the phone with this
   *  person right now, so their in-app alarm audio is cut for the length
   *  of the call. The live human voice beats the chime.
   *
   *  ⚠ IT IS STRONGER THAN `voiceCallQuietUntil` ABOVE ON PURPOSE, AND
   *  NARROWER. Cycle 381 skips one 8.2s urgent-low one-shot for EVERYONE
   *  in the household at dial time, because the platform could not tell
   *  which handset it was ringing. This cuts ALL in-app alarm audio for
   *  the ONE account that answered, keyed to the call's real status. The
   *  cycle-381 caution ("silencing a continuous alarm is a materially
   *  bigger call") is answered rather than ignored: this is bounded by a
   *  server ceiling, ends the moment the call does, applies to exactly
   *  the person holding the phone to their ear, and never touches the
   *  kiosk or any display.
   *
   *  Null/absent/past ⇒ no suppression, which is the fail-open direction.
   *  The KIOSK ignores this outright — a kiosk device session
   *  authenticates as the owner, so the user id WILL match and the
   *  `kioskUrgentRepeat` guard is what protects it. */
  callQuietUntil?: number | null;
}

// Cycle 112: belt-and-suspenders staleness guard on the one-shot path.
// A live `alert` frame is always fresh, so this never trips in normal
// operation — it only matters if a future code path mislabels a seed as
// a live event. If the event alert's `fired_at` (fallback `triggered_at`)
// is older than this when the event is handled, the one-shot is skipped.
// Cycle 429: the crossing chime's ten-minute guard is now the ONE shared
// event-cue age bound (`lib/alarmCue.ts`, thirty seconds) — every one-shot
// cue reads it, not only this one. Kept under its old name for readers.
export const STALE_ALERT_AUDIO_MS = CUE_EVENT_AGE_MS;

// Cycle 216/232: recurring-cadence intervals (modeled on the cycle-33
// no-data interval pattern — driven by the CURRENT sustained zone, never
// fired on arm or on a snapshot seed, so they're replay-safe across a
// deploy reload / cycle-231 snapshot-seed fix and resume from current
// state). **Cycle 232/240: the High chime plays `high.wav` ONCE on
// crossing into HIGH-SIDE (`high` OR `urgent_high`) — restored in cycle 240
// (cycle 232 had wrongly dropped the entry chime) — then the sustained
// cadence below replays it every 60 min while the high persists (first
// cadence re-fire one full interval after arming).** So: chime at crossing,
// then hourly. (Cycle 232 had moved the high cadence 30→60 min + folded
// urgent_high in; cycle 240 only adds back the immediate entry chime.)
// The in-range sound is now ONLY the return-to-range one-shot chime
// (cycle 33 bridge, below), which fires on entry into range from a high
// or low; the recurring 33-min in-range chime was removed (cycle 235.3).
export const HIGH_CADENCE_INTERVAL_MS = 60 * 60_000;

// ─── Cycle 374: the kiosk urgent-low repeating cue ────────────────────
// The urgent-low siren no longer loops on any surface; on the KIOSK it
// re-fires on an escalating interval instead, because the kiosk is the one
// surface guaranteed to be in the room AND the one with no push and no
// phone call behind it.
//
// Named to match the documented knob `SUGALARM_KIOSK_URGENT_REPEAT_SECONDS`.
// It is a client-side constant rather than an env var because the SPA is a
// static bundle with no per-deploy client env seam; making it operator-
// tunable belongs with the other per-member `members.settings` keys, which
// is a settings cycle, not an audio one.
export const KIOSK_URGENT_REPEAT_MS = 30 * 1_000;
// The bounded escalation ceiling — the gap never tightens past this, so an
// unaddressed alarm becomes more insistent without collapsing back into the
// continuous loop this cycle removed.
export const KIOSK_URGENT_REPEAT_FLOOR_MS = 10 * 1_000;
// How much each unanswered repeat tightens the gap.
export const KIOSK_URGENT_REPEAT_STEP_MS = 5 * 1_000;

/** Gap before the Nth (0-based) kiosk re-fire of the urgent-low cue.
 *
 *  Pure so the escalation curve is unit-testable without a DOM. Two
 *  properties are load-bearing and pinned by tests: it never DROPS BELOW
 *  the floor (a gap racing toward zero would collapse straight back into
 *  the continuous loop this cycle removed, and at zero would be a busy
 *  loop), and it never RISES above the initial interval (an unaddressed
 *  alarm must get more insistent, never less). */
export function kioskUrgentRepeatGapMs(step: number): number {
  const n = Number.isFinite(step) && step > 0 ? Math.floor(step) : 0;
  return Math.max(
    KIOSK_URGENT_REPEAT_FLOOR_MS,
    KIOSK_URGENT_REPEAT_MS - n * KIOSK_URGENT_REPEAT_STEP_MS,
  );
}

/** The step counter after one kiosk cue has fired (cycle 391).
 *
 *  Pure so the presence gate's two load-bearing properties are pinnable
 *  without a DOM: it NEVER rewinds (an alarm can only get more insistent
 *  over its life, never less — the cycle-374 property, which an earlier
 *  draft of this gate broke by resetting the gap to the starting value on
 *  any touch), and while somebody is at the screen it simply stops
 *  advancing, so a caregiver present from the start holds the cue at
 *  exactly the starting interval. */
export function nextKioskRepeatStep(
  step: number,
  touchedRecently: boolean,
): number {
  const n = Number.isFinite(step) && step > 0 ? Math.floor(step) : 0;
  return touchedRecently ? n : n + 1;
}

/** Which urgent-low playback the surface gets (cycle 374).
 *
 *  Exists as a named function so the de-loop is greppable and testable
 *  rather than an inline ternary: it is the one place that decides whether
 *  a surface keeps a persistent audible cue. */
export function urgentLowPlaybackMode(
  isKiosk: boolean,
): "kiosk-repeat" | "once" {
  return isKiosk ? "kiosk-repeat" : "once";
}

/** Cycle 381 — should THIS surface skip the in-app urgent-low one-shot?
 *
 *  The problem it solves: the APNS critical alert and the in-app cue play
 *  the SAME audio file (cycle 374.1 installed one source to both), so a
 *  caregiver who picks the phone up because of the notification hears the
 *  identical 8.2s sound a second time, over the incoming call.
 *
 *  THREE PROPERTIES, EACH DELIBERATE:
 *   - THE KIOSK IS NEVER SUPPRESSED. It is not the device being called, and
 *     cycle 374 pinned its repeating cue as the floor — the one surface
 *     guaranteed to be in the room with no push or call behind it.
 *   - IT IS A DEADLINE, NOT A FLAG. A "call in progress" boolean is the
 *     cycle-366 stuck-`in_progress` bug again (16 escalations stuck, one for
 *     29 days); a deadline expires with no one having to clear it.
 *   - IT FAILS OPEN, TOWARD SOUND. Null, unparseable, or past ⇒ play. A lost
 *     frame, a dropped socket, or an older client all keep sounding;
 *     silence requires a positive, expiring instruction to arrive.
 *
 *  Pure so the rule is testable without audio elements or a socket. */
export function inAppCueQuiet(
  now: number,
  quietUntil: number | null | undefined,
  isKiosk: boolean,
): boolean {
  if (isKiosk) return false;
  if (quietUntil == null || !Number.isFinite(quietUntil)) return false;
  return now < quietUntil;
}

/** Cycle 403: is THIS session's own account on the phone with us right now?
 *
 *  While true, a mobile / web session cuts its in-app alarm audio — the
 *  live human voice beats the chime, and the person hearing it is the one
 *  we are literally talking to. It DELEGATES to `inAppCueQuiet` rather
 *  than restating the comparison, so the two rules can never drift apart
 *  about what "quiet" means; they differ only in what they suppress and
 *  in who they apply to, which is the caller's business, not the rule's.
 *
 *  ⚠ THE KIOSK IS EXCLUDED HERE, AND THE `user_id` MATCH IS NOT WHAT
 *  PROTECTS IT. A kiosk device session authenticates as the household
 *  owner, so if the owner's account is the one being called the id WILL
 *  match. The kiosk is a device on a counter, not the phone in the
 *  caregiver's hand, and cycle 374 pinned its cue as the never-silent
 *  floor — so `isKiosk` short-circuits before anything else is read.
 *
 *  ⚠ SOUND ONLY. Nothing downstream of this removes a banner, a hero or a
 *  strip from any screen. The display never stops anywhere.
 *
 *  Fail-open toward sound: null, unparseable, or past ⇒ audible. Pure, so
 *  the rule is testable without audio elements or a socket. */
export function callQuietActive(
  now: number,
  until: number | null | undefined,
  isKiosk: boolean,
): boolean {
  return inAppCueQuiet(now, until, isKiosk);
}

/** Cycle 406: the ONE key under which a `call_quiet` deadline is stored
 *  and read — account AND member together.
 *
 *  ⚠ MEMBER-SCOPED, DELIBERATELY. Cycle 403 keyed the deadline on the
 *  account alone, so a call about the member cut in-app audio for Ben's alarm on
 *  the same handset. Arc 3's standing invariant is "the member's quiet never
 *  covers Ben": the reducer stores under this key and `App.tsx` reads it
 *  for the signed-in user AND the active member, so a frame about another
 *  member is stored but never consulted. Pure, so the scoping is testable
 *  without a socket. Either half missing ⇒ null ⇒ no key ⇒ audible. */
export function callQuietKey(
  userId: string | null | undefined,
  memberId: string | null | undefined,
): string | null {
  if (!userId || !memberId) return null;
  return `${userId}:${memberId}`;
}

// Cycle 360.5: the kiosk in-app no-data alarm fires only after the feed has
// been dark for this long — deliberately LONGER than the ~10-min provider
// staleness window (`isStale`) that greys the hero, so a brief gap never
// blares a false alarm on the always-on tablet.
export const NO_DATA_ALARM_MS = 15 * 60_000;

const SRC = {
  low: "/sounds/lowNotify.wav",
  high: "/sounds/high.wav",
  urgentLow: "/sounds/urgentLow.wav",
  prediction: "/sounds/predictionAlert.mp3",
  // Cycle 33: return-to-range + no-data audio paths.
  normal: "/sounds/normal.wav",
  noData: "/sounds/noData.wav",
  // Cycle 243.5: the reminder chime (cycle-239 asset). Plays once when a
  // reminder fires, BEFORE the spoken TTS. Independent of `alerts_enabled`.
  reminder: "/sounds/sugalarm_reminder.mp3",
  // Cycle 391: the kiosk soft tick during a treating window. REUSES the
  // bundled cycle-100 wake chime — no new asset, no new file, no new
  // fetch. It is the only short, neutral, non-alarm sound in the bundle:
  // every alarm skin is the alarm itself (wrong — the window is supposed
  // to have quieted it), and `normal.wav` is the RETURN-TO-RANGE chime,
  // which would say "recovered" every few minutes about a child who is
  // still low. A neutral blip says only "this is still running", which
  // is exactly the claim being made.
  treatingTick: "/sounds/wake.wav",
} as const;

// Cycle 391: one short cue every five minutes while a treating window is
// open on the KIOSK. The interval is deliberately long and the gain
// deliberately low — this is not an alarm, it is proof of life. It costs
// nothing against somebody working the problem, and it covers the real
// failure mode: the caregiver taps Treating, gets pulled into something
// else, and the room concludes the system is off.
export const TREATING_TICK_INTERVAL_MS = 5 * 60 * 1000;
// ⚠ THE FIRST TICK LEADS THE CADENCE, AND THAT IS NOT A STYLE CHOICE.
// With the first cue scheduled a full interval out, a 300-second window
// produced its first tick at the exact instant the window ended and the
// teardown cleared the timer — ZERO audible cues. And because the
// engagement cap forces the THIRD and every later window of an episode
// down to 300 seconds, the never-silent floor evaporated in precisely the
// repeat-tap case the cap exists to police. Leading by a minute
// guarantees at least one cue in the shortest window we can issue, while
// still never sounding more often than once per interval afterwards
// (5 min → a tick at 1:00; 20 min → 1:00, 6:00, 11:00, 16:00).
export const TREATING_TICK_FIRST_MS = 60 * 1000;
// Reduced gain. NOT applied to any alarm element — only this tick.
export const TREATING_TICK_VOLUME = 0.35;

/** Cycle 410.1 — ONE soft chirp when a lead window OPENS. Pure, so the rule
 *  is unit-testable: the rising edge of `audioLead`, on the KIOSK only
 *  (phones already got the critical push), and NOT on a descent where a
 *  `low` alarm is already audible. Once per window by construction (an
 *  edge fires once). It can only ever exist when the server opened a
 *  window, which only happens with `SUGALARM_AUDIO_LEAD_ENABLED` on for
 *  the household — a dark household never has the edge. */
export function leadOpenChirp(
  wasLead: boolean,
  isLead: boolean,
  kiosk: boolean,
  audible: boolean,
): boolean {
  return !wasLead && isLead && kiosk && !audible;
}

// Cycle 391: how long a kiosk touch holds the cycle-374 cue at its
// STARTING interval. The tightening exists to reach a household that has
// not responded; it should not nag somebody visibly standing at the
// screen. A narrow gate on one input — the floor, the step and the
// untouched-kiosk behaviour are all unchanged.
export const KIOSK_PRESENCE_HOLD_MS = 60 * 1000;

// Cycle 34.11: the noData chime is disabled. Constants are kept
// commented out so the cycle-33 bridge can be restored verbatim if a
// future cycle wants it back.
// const NO_DATA_INTERVAL_MS = 10 * 60 * 1000;

// ─── Cycle 464.2: the element gate ──────────────────────────────────
// ⚠ A GESTURE CAN ONLY EVER UNLOCK AUDIO, NEVER SILENCE OR SWALLOW A CUE.
// The autoplay unlock below primes every element with a MUTED play() and a
// pause() on the first gesture after a page load. Until this cycle it did so
// to EVERY element, including one that was sounding or one a cue claimed
// inside that window: the cue played muted, resolved as started (so its
// one-shot latch held), and the prime's restore then paused it. For the
// no-data tone that silenced the whole cue for the rest of the outage while
// the banner stayed up (found in cycle 464.1 on the kiosk, which reloads
// after every deploy and allows autoplay, so one touch during the next tone
// was enough). Every play and pause in this hook goes through the three
// helpers below, which share one gate per element:
//   * `claimed`: a cue or a loop owns the element from its play() until OUR
//     OWN pause (`safePause`) or the media's natural end (`ended`). The prime
//     never touches a claimed or sounding element and restores only what it
//     changed on an idle one.
//   * `priming`: the prime is in flight on the element. A play requested
//     meanwhile is QUEUED and runs, unmuted, the moment the prime has
//     restored the element; it is never issued into the muted window.
//   * The one-shot latch (`safePlayReporting`'s verdict) is set from the
//     element's real outcome: play() resolved with the element unmuted,
//     still claimed (nothing of ours paused it meanwhile) and not paused.
// Pinned by `web/src/lib/__tests__/cycle464_2UnlockPrime.test.ts`, red on
// the pre-464.2 code.
interface PendingPlay {
  /** Issue the play now (the prime has restored the element). */
  run: () => void;
  /** The play will never be issued (our own pause dropped it). */
  cancel: () => void;
}

interface ElementGate {
  claimed: boolean;
  priming: boolean;
  pending: PendingPlay[];
  /** Bumped by every `safePause` (our own cut), never by a natural end: a
   *  follow-up a cue owes itself (the prediction's second play) compares it
   *  and replays nothing after a cut. */
  gen: number;
}

/** How long a prime may wait on one element's muted play() before the
 *  element is restored and handed on regardless (security review, 464.2): a
 *  media load that stalls without an error never settles its play() promise,
 *  and that must delay the unlock, never hold an alarm element's plays. */
const PRIME_TIMEOUT_MS = 2000;

/** The play was REJECTED and left the element paused: nothing is sounding,
 *  so the claim is released and the next prime may unlock the element (on a
 *  per-element unlock browser the muted prime inside the gesture is what
 *  lets the unlock retry's play() succeed). */
function releaseIfRejected(a: HTMLAudioElement): void {
  if (!a.paused) return;
  const g = gateOf(a);
  g.claimed = false;
}

const elementGates = new WeakMap<HTMLAudioElement, ElementGate>();

function gateOf(a: HTMLAudioElement): ElementGate {
  let g = elementGates.get(a);
  if (!g) {
    g = { claimed: false, priming: false, pending: [], gen: 0 };
    elementGates.set(a, g);
    // The media reached its natural end: the claim is released. A loop never
    // ends on its own, so a loop stays claimed until `safePause`. A source
    // swap (`el.load()` after a custom-sound change or its error fallback)
    // empties the element without `ended`, so `emptied` releases it too.
    // Both are queued media tasks that can land AFTER a `safePlay` has
    // already restarted the element (the looping swap, a re-play in the same
    // task), so neither releases an element that is sounding by then.
    const release = () => {
      const gate = elementGates.get(a);
      if (gate && a.paused) gate.claimed = false;
    };
    a.addEventListener("ended", release);
    a.addEventListener("emptied", release);
  }
  return g;
}

/** The prime may touch this element: paused, not ended, and not already
 *  being primed. A sounding element is excluded by `paused` alone; a claim on
 *  a PAUSED element can only mean a play the browser refused or interrupted
 *  (an OS audio interruption, a media error), and that element needs the
 *  prime most (safety review round 2, 464.2). The claim still feeds the
 *  verdict. An ENDED element is excluded too (astra round 4): it has just
 *  played to its end, so it needs no unlock, and its `ended` event may still
 *  be in flight (Chromium flips `paused` and `ended` when it detects the end
 *  and dispatches the event in a later task); a play() on it would seek to
 *  the start and clear `ended`, and a follow-up the cue owes itself (the
 *  prediction's second play) would then take its own end for a stale one. */
function primeMayTouch(a: HTMLAudioElement): boolean {
  const g = gateOf(a);
  return !g.priming && a.paused && !a.ended;
}

function makeAudio(src: string): HTMLAudioElement {
  const a = new Audio(src);
  a.preload = "auto";
  gateOf(a);
  return a;
}

function startPlay(a: HTMLAudioElement): Promise<void> | undefined {
  try {
    a.currentTime = 0;
  } catch {
    // Some browsers throw on currentTime= before metadata loads.
  }
  return a.play();
}

function safePlay(a: HTMLAudioElement | null): void {
  if (!a) return;
  const g = gateOf(a);
  g.claimed = true;
  const run = () => {
    const p = startPlay(a);
    if (p && typeof p.catch === "function") {
      p.catch((err: unknown) => {
        // Autoplay policies block playback until the user has interacted
        // with the page. After the first tap/click on the kiosk these
        // succeed. Silent debug only — no UI surface.
        // eslint-disable-next-line no-console
        console.debug("[alarm] play() rejected", err);
        releaseIfRejected(a);
      });
    }
  };
  // Cycle 464.2: inside the prime's window the play waits for the restore.
  if (g.priming) {
    g.pending.push({ run, cancel: () => {} });
    return;
  }
  run();
}

/** Cycle 386: like `safePlay`, but reports whether playback actually
 *  STARTED. A one-shot cue can't tell "already played" from "blocked by
 *  the autoplay policy" by looking at `el.paused` — both read paused —
 *  so a one-shot that wants an unlock retry needs the play() verdict. */
function safePlayReporting(a: HTMLAudioElement | null): Promise<boolean> {
  if (!a) return Promise.resolve(false);
  const g = gateOf(a);
  g.claimed = true;
  // Cycle 464.2: the verdict is the element's REAL outcome. "Started" means
  // play() resolved with the element unmuted (never inside the prime's
  // muted window), still claimed (nothing of ours paused it meanwhile) and
  // not paused. Anything else releases the one-shot latch so the cue is
  // re-fired, never latched silent.
  const run = (): Promise<boolean> => {
    const p = startPlay(a);
    if (!p || typeof p.then !== "function") {
      return Promise.resolve(!a.muted && g.claimed && !a.paused);
    }
    return p.then(
      () => !a.muted && g.claimed && !a.paused,
      (err: unknown) => {
        // eslint-disable-next-line no-console
        console.debug("[alarm] play() rejected", err);
        releaseIfRejected(a);
        return false;
      },
    );
  };
  if (g.priming) {
    // Queued behind the prime; a `safePause` meanwhile settles it as "not
    // started" so the caller's latch is released, never left hanging.
    return new Promise<boolean>((resolve) => {
      g.pending.push({
        run: () => {
          void run().then(resolve);
        },
        cancel: () => resolve(false),
      });
    });
  }
  return run();
}

function safePause(a: HTMLAudioElement | null): void {
  if (!a) return;
  // Cycle 464.2: OUR OWN pause releases the claim and drops any play that
  // was waiting for the prime (the caller wanted the element quiet); a
  // dropped reporting play settles as "not started".
  const g = gateOf(a);
  g.claimed = false;
  g.gen += 1;
  const dropped = g.pending;
  g.pending = [];
  for (const p of dropped) p.cancel();
  a.pause();
  a.loop = false;
  try {
    a.currentTime = 0;
  } catch {
    /* ignore */
  }
}

export function useAlarmSounds(opts: UseAlarmSoundsOpts): AlarmSounds {
  const { enabled, socket, activeMemberId, episode, thresholds } = opts;
  const soundSelection = opts.soundSelection;
  const noDataLoopEnabled = opts.noDataLoopEnabled ?? false;
  const serviceLocked = opts.serviceLocked ?? false;
  const sensorPauseActive = opts.sensorPauseActive ?? false;
  // Cycle 374: kiosk-only escalating repeat of the urgent-low cue.
  const kioskUrgentRepeat = opts.kioskUrgentRepeat ?? false;
  const treatingActive = opts.treatingActive ?? false;
  const voiceCallQuietUntil = opts.voiceCallQuietUntil ?? null;
  const callQuietUntil = opts.callQuietUntil ?? null;

  // ⚠ THE QUIET HAS TO EXPIRE ON ITS OWN, WHICH MEANS SOMETHING HAS TO
  // WAKE THE HOOK AT THE DEADLINE. `callQuietUntil` is read inside a
  // derivation, so with no re-render at the moment it lapses the audio
  // would stay cut until the next unrelated frame happened to arrive —
  // turning a bounded quiet into an open-ended one on a quiet socket.
  // One timeout, armed only while a deadline is genuinely in the future,
  // cleared on every change. This can only ever RESTORE sound.
  const [, bumpQuietTick] = useState(0);
  useEffect(() => {
    if (callQuietUntil == null || !Number.isFinite(callQuietUntil)) return;
    const ms = callQuietUntil - Date.now();
    if (ms <= 0) return;
    const id = window.setTimeout(() => bumpQuietTick((n) => n + 1), ms);
    return () => window.clearTimeout(id);
  }, [callQuietUntil]);

  // The single derivation every consumer below reads. Recomputed on each
  // render, and the timeout above guarantees a render at the deadline.
  const callQuiet = callQuietActive(
    Date.now(),
    callQuietUntil,
    kioskUrgentRepeat,
  );

  // Pre-create the audio elements once.
  const lowRef = useRef<HTMLAudioElement | null>(null);
  const highRef = useRef<HTMLAudioElement | null>(null);
  const urgentLowRef = useRef<HTMLAudioElement | null>(null);
  // Cycle 242: urgent_high gets its OWN element (it used to share highRef
  // via startUrgentHighLoop / the high cadence) so high and urgent_high can
  // carry DISTINCT custom sounds. Default file is still high.wav.
  const urgentHighRef = useRef<HTMLAudioElement | null>(null);
  const predictionRef = useRef<HTMLAudioElement | null>(null);
  // Cycle 464.2: the prediction cue's request token (its second play is
  // owed to the latest request only), and the one armed follow-up listener
  // (astra round 4): a new request replaces it, the unmount removes it, so a
  // follow-up that stayed armed across a stale `ended` never accumulates or
  // outlives the hook.
  const predictionSeqRef = useRef(0);
  const predictionFollowUpRef = useRef<{ el: HTMLAudioElement; fn: () => void } | null>(null);
  const disarmPredictionFollowUp = useCallback(() => {
    const armed = predictionFollowUpRef.current;
    if (!armed) return;
    predictionFollowUpRef.current = null;
    armed.el.removeEventListener("ended", armed.fn);
  }, []);
  // Cycle 33: return-to-range (normal) + no-data.
  const normalRef = useRef<HTMLAudioElement | null>(null);
  const noDataRef = useRef<HTMLAudioElement | null>(null);
  // Cycle 242: a dedicated owner-held element for the Settings preview, so
  // a preview never disturbs a real alarm element's src/loop state.
  const previewRef = useRef<HTMLAudioElement | null>(null);
  // Cycle 243.5: the reminder chime element (plays before the spoken TTS).
  const reminderRef = useRef<HTMLAudioElement | null>(null);
  // Cycle 391: the kiosk treating-window soft tick. Its OWN element, held
  // by the same single owner as every other sound, so setting its reduced
  // volume can never bleed into an alarm element.
  const treatingTickRef = useRef<HTMLAudioElement | null>(null);

  if (lowRef.current == null && typeof window !== "undefined") {
    lowRef.current = makeAudio(SRC.low);
    highRef.current = makeAudio(SRC.high);
    urgentLowRef.current = makeAudio(SRC.urgentLow);
    urgentHighRef.current = makeAudio(SRC.high);
    predictionRef.current = makeAudio(SRC.prediction);
    normalRef.current = makeAudio(SRC.normal);
    noDataRef.current = makeAudio(SRC.noData);
    previewRef.current = makeAudio(SRC.high);
    reminderRef.current = makeAudio(SRC.reminder);
    treatingTickRef.current = makeAudio(SRC.treatingTick);
    treatingTickRef.current.volume = TREATING_TICK_VOLUME;
  }

  const [loopActive, setLoopActive] = useState(false);
  // Cycle 451.1: the off-kiosk urgent-low one-shot's FIRE edge. See the
  // interface note: it is bumped only where the cue really plays.
  const [urgentCueSeq, setUrgentCueSeq] = useState(0);
  // Tracks whether the browser's autoplay policy lets us play sound.
  // Flips true on the first user gesture anywhere in the document.
  // The bridge below depends on this so a play() that was rejected
  // pre-gesture is retried as soon as the user taps.
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  // Cycle 216: a coarse ticking clock so the new High / In-range
  // cadences clear themselves when the reading goes stale even without
  // a fresh WS frame arriving (a quiet sensor must stop the chime).
  const nowMs = useNow(30_000);

  // ─── Audio autoplay-policy unlock ────────────────────────────────
  // Browsers block HTMLMediaElement.play() until the user has
  // interacted with the page; for an alarm fired off a WebSocket
  // frame (no user gesture in scope) Chrome / Safari reject silently
  // and the kiosk goes mute. Prime each element with a muted
  // play+pause on the first pointerdown / keydown / touchstart so
  // subsequent .play() calls succeed regardless of gesture context.
  //
  // Race: setAudioUnlocked must wait for every prime's pause/restore
  // to finish. Otherwise the bridge below starts a real play() on
  // the unlock-state change and the prime's `restore()` then pauses
  // it a few ms later, leaving the kiosk silent.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let primed = false;
    const unlock = async () => {
      if (primed) return;
      primed = true;
      const els = [
        lowRef.current,
        highRef.current,
        urgentLowRef.current,
        // Cycle 242: urgent_high's own element + the preview element.
        urgentHighRef.current,
        previewRef.current,
        // Cycle 243.5: the reminder chime element.
        reminderRef.current,
        predictionRef.current,
        // Cycle 33: prime so the autoplay policy doesn't reject the
        // first stale-fire or return-to-range chime.
        normalRef.current,
        noDataRef.current,
        // Cycle 391: prime the treating tick too, so the first cue five
        // minutes into a window is not the one the autoplay policy eats.
        treatingTickRef.current,
      ];
      // Run every prime to completion BEFORE flipping audioUnlocked.
      // ⚠ Cycle 464.2: the prime touches IDLE elements only. One that is
      // sounding, or that a cue has claimed, is left exactly as it is: a
      // gesture may unlock audio, never silence or swallow a cue (the
      // element-gate comment above `makeAudio`). On an idle element it
      // restores only what it changed, then hands the element to any play
      // that was requested while it held it.
      await Promise.all(
        els.map(async (el) => {
          if (!el) return;
          if (!primeMayTouch(el)) return;
          const g = gateOf(el);
          g.priming = true;
          const wasMuted = el.muted;
          try {
            el.muted = true;
            try {
              const p = el.play();
              // Bounded: a load that stalls without an error never settles
              // play(); the element is restored and handed on anyway.
              if (p && typeof p.then === "function") {
                await Promise.race([
                  p,
                  new Promise<void>((resolve) => window.setTimeout(resolve, PRIME_TIMEOUT_MS)),
                ]);
              }
            } catch {
              /* play may reject on some browsers even when muted */
            }
            try {
              el.pause();
              el.currentTime = 0;
            } catch {
              /* ignore */
            }
          } finally {
            // Whatever happened above, the element is restored, released
            // from the prime and handed to any play that waited for it.
            el.muted = wasMuted;
            g.priming = false;
            const pending = g.pending;
            g.pending = [];
            for (const play of pending) play.run();
          }
        }),
      );
      setAudioUnlocked(true);
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []);

  // Keep `enabled` accessible in event handlers without re-binding them.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Keep the live episode accessible inside stable preview callbacks.
  const episodeRef = useRef(episode);
  episodeRef.current = episode;

  // ─── Cycle 242: per-zone src selection + mandatory default fallback ──
  // Resolve each zone element's `src` from the saved setting (the manifest
  // maps id → file; unknown/"default" → the zone's original file). On a
  // load/decode `error` swap that element to its GUARANTEED default file so
  // the alarm/recovery cue is NEVER silent because of a custom choice (a
  // hard safety rule). If the failing element was actively looping (an
  // urgent loop), restart the loop on the default so live audio continues.
  useEffect(() => {
    const cleanups: Array<() => void> = [];
    const apply = (zone: AlarmZone, el: HTMLAudioElement | null) => {
      if (!el) return;
      const want = resolveAlarmSoundFile(zone, soundSelection?.[zone]);
      const def = ALARM_SOUND_DEFAULT_FILE[zone];
      const onError = () => {
        if (el.src.endsWith(def)) {
          // eslint-disable-next-line no-console
          console.warn(`[alarm] default sound failed to load (${zone}): ${def}`);
          return;
        }
        // eslint-disable-next-line no-console
        console.warn(
          `[alarm] custom sound failed (${zone}); falling back to default`,
        );
        const wasLooping = el.loop;
        el.src = def;
        el.load();
        if (wasLooping) {
          el.loop = true;
          safePlay(el);
        }
      };
      el.addEventListener("error", onError);
      cleanups.push(() => el.removeEventListener("error", onError));
      if (!el.src.endsWith(want)) {
        const wasLooping = el.loop;
        el.src = want;
        el.load();
        if (wasLooping) {
          el.loop = true;
          safePlay(el);
        }
      }
    };
    apply("urgent_low", urgentLowRef.current);
    apply("low", lowRef.current);
    apply("inrange", normalRef.current);
    apply("high", highRef.current);
    apply("urgent_high", urgentHighRef.current);
    return () => {
      cleanups.forEach((c) => c());
    };
    // Key on the five resolved ids, not the object identity (stable churn).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    soundSelection?.urgent_low,
    soundSelection?.low,
    soundSelection?.inrange,
    soundSelection?.high,
    soundSelection?.urgent_high,
  ]);

  // ─── Cycle 374: kiosk urgent-low repeating cue ────────────────────
  // Handle for the escalating re-fire timer. Cleared by `stopAllLoops`,
  // which the cycle-312 falling-edge hard-cut already calls on Snooze /
  // Stop / episode end — so the repeat can never outlive `audioOn`, and
  // no new "who stops this" path is introduced.
  const kioskRepeatTimerRef = useRef<number | null>(null);
  const kioskRepeatCountRef = useRef(0);

  // ─── Cycle 391: presence gate on the cycle-374 escalation ──────────
  // The cue HOLDS at its starting interval while the kiosk has been
  // touched in the last `KIOSK_PRESENCE_HOLD_MS`, and resumes tightening
  // once it has not. The floor, the step size and the untouched-kiosk
  // behaviour are all untouched.
  //
  // ⚠ "HOLD" IS IMPLEMENTED AS FREEZING THE STEP COUNTER, NOT AS RESETTING
  // THE GAP, AND THAT IS A DELIBERATE NARROWING OF THE INSTRUCTION.
  //
  // Read literally, "hold it at the starting interval while the kiosk has
  // been touched" means an alarm that had already escalated to the 10 s
  // floor jumps back to 30 s on any touch. But the listener is a
  // window-level one that cannot tell a caregiver from a palm on a
  // nightstand tablet or somebody scrolling the chart — so read that way
  // the rule lets a stray touch make an UNADDRESSED urgent low three
  // times less insistent, app-wide, with no treating window involved.
  // Weakening a live hypo alarm is not something a presence heuristic
  // gets to do as a side effect.
  //
  // Freezing the counter gives the instruction its intent without that
  // property: a caregiver present from the start keeps the counter at 0,
  // so the cue holds at exactly the 30 s STARTING interval as specified,
  // and it simply stops tightening further while they are there. An alarm
  // that already escalated holds where it got to and never relaxes. The
  // curve stays monotonically non-increasing, which is the cycle-374
  // property, and the moment touching stops the tightening resumes from
  // the step it had reached.
  //
  // Flagged in the CHANGELOG. Widening it back to the literal reading is
  // a one-line change if the operator wants it.
  //
  // Passive capture-phase listeners that ONLY observe (no
  // preventDefault, no stopPropagation), so nothing about touch handling
  // anywhere in the app changes.
  const lastTouchAtRef = useRef(0);
  useEffect(() => {
    if (!kioskUrgentRepeat || typeof window === "undefined") return;
    const mark = () => {
      lastTouchAtRef.current = Date.now();
    };
    const opts = { capture: true, passive: true } as const;
    window.addEventListener("pointerdown", mark, opts);
    window.addEventListener("keydown", mark, opts);
    return () => {
      window.removeEventListener("pointerdown", mark, opts);
      window.removeEventListener("keydown", mark, opts);
    };
  }, [kioskUrgentRepeat]);

  const clearKioskUrgentRepeat = useCallback(() => {
    if (kioskRepeatTimerRef.current != null) {
      window.clearTimeout(kioskRepeatTimerRef.current);
      kioskRepeatTimerRef.current = null;
    }
    kioskRepeatCountRef.current = 0;
  }, []);

  const stopAllLoops = useCallback(() => {
    clearKioskUrgentRepeat();
    safePause(lowRef.current);
    safePause(urgentLowRef.current);
    safePause(highRef.current);
    // Cycle 242: urgent_high now has its own element.
    safePause(urgentHighRef.current);
    setLoopActive(false);
  }, [clearKioskUrgentRepeat]);

  const playLow = useCallback(() => {
    if (!enabledRef.current) return;
    safePlay(lowRef.current);
  }, []);

  const playHigh = useCallback(() => {
    if (!enabledRef.current) return;
    safePlay(highRef.current);
  }, []);

  // Cycle 242: urgent_high one-shot on its OWN element (distinct sound).
  const playUrgentHigh = useCallback(() => {
    if (!enabledRef.current) return;
    safePlay(urgentHighRef.current);
  }, []);

  const startLowLoop = useCallback(() => {
    if (!enabledRef.current) return;
    const a = lowRef.current;
    if (!a) return;
    a.loop = true;
    safePlay(a);
    setLoopActive(true);
  }, []);

  const startUrgentLowLoop = useCallback(() => {
    if (!enabledRef.current) return;
    const a = urgentLowRef.current;
    if (!a) return;
    a.loop = true;
    safePlay(a);
    setLoopActive(true);
  }, []);

  /** Cycle 374 — play the urgent-low cue ONCE (no `loop`).
   *
   *  The de-loop is urgent-low only and is an INTENTIONAL, operator-
   *  approved reduction of on-device persistence: a ~30-second siren
   *  repeating without end is sensory overload for the person who has to
   *  stay calm and treat a child. Persistence moved, it was not deleted —
   *  the kiosk keeps a repeating cue (below), and every other surface is
   *  backed by the push cadence and the cycle-371/373 call sequence. */
  const playUrgentLowOnce = useCallback(() => {
    if (!enabledRef.current) return;
    const a = urgentLowRef.current;
    if (!a) return;
    a.loop = false;
    safePlay(a);
    setLoopActive(false);
  }, []);

  /** Cycle 374 — the KIOSK repeating cue. PINNED SAFETY FLOOR.
   *
   *  The kiosk is the one surface guaranteed to be in the room, and it is
   *  the surface with no push and no phone call behind it. De-looping it
   *  to a single short beep would leave a room that can go silent during
   *  an untreated hypo, so the kiosk re-fires the cue until the episode is
   *  acknowledged or recovers.
   *
   *  ESCALATION IS IN THE INTERVAL, NOT THE VOLUME, and that is
   *  deliberate: starting quiet to "escalate" upward would make the FIRST
   *  cue — the one most likely to be the only one anybody hears — the
   *  quietest. Every cue plays at full volume; an unaddressed alarm simply
   *  gets more insistent, tightening from `KIOSK_URGENT_REPEAT_MS` toward
   *  `KIOSK_URGENT_REPEAT_FLOOR_MS`, which is the bounded ceiling.
   *
   *  Self-rescheduling `setTimeout` rather than `setInterval` so the gap
   *  can shrink, and so a webview that suspends timers in the background
   *  resumes cleanly instead of firing a backlog. */
  const startKioskUrgentRepeat = useCallback(() => {
    if (!enabledRef.current) return;
    const a = urgentLowRef.current;
    if (!a) return;
    clearKioskUrgentRepeat();
    a.loop = false;
    safePlay(a);
    setLoopActive(true);

    const schedule = () => {
      const gap = kioskUrgentRepeatGapMs(kioskRepeatCountRef.current);
      kioskRepeatTimerRef.current = window.setTimeout(() => {
        // `stopAllLoops` clears the timer, so reaching here means the
        // episode is still sounding.
        if (!enabledRef.current) return;
        const el = urgentLowRef.current;
        if (!el) return;
        el.loop = false;
        try {
          el.currentTime = 0;
        } catch {
          /* some browsers throw before metadata loads — harmless */
        }
        safePlay(el);
        // The hold, evaluated at FIRE time (not schedule time, which
        // would be up to 30 s stale): while the kiosk is being touched
        // the step does not advance, so the gap stays where it is
        // instead of tightening. It is never rewound.
        kioskRepeatCountRef.current = nextKioskRepeatStep(
          kioskRepeatCountRef.current,
          Date.now() - lastTouchAtRef.current < KIOSK_PRESENCE_HOLD_MS,
        );
        schedule();
      }, gap);
    };
    schedule();
  }, [clearKioskUrgentRepeat]);

  // ─── Cycle 391: the kiosk soft tick ───────────────────────────────
  //
  // THE KIOSK NEVER ACTUALLY GOES SILENT. While a treating window is
  // open the escalating cue stands down (the episode bridge already
  // stopped it — `audioOn` factors in `!treating`), and this takes over:
  // one short cue every five minutes at reduced gain, through the SAME
  // `safePlay` path and the SAME single `<audio>` owner as everything
  // else. Kiosk only, and only while an urgent-low episode is genuinely
  // live under a window.
  //
  // Self-rescheduling `setTimeout` rather than `setInterval`, matching
  // the cycle-374 cue: a webview that suspends timers in the background
  // resumes cleanly instead of firing a backlog of ticks at once.
  const treatingTickTimerRef = useRef<number | null>(null);
  const clearTreatingTick = useCallback(() => {
    if (treatingTickTimerRef.current != null) {
      window.clearTimeout(treatingTickTimerRef.current);
      treatingTickTimerRef.current = null;
    }
    // A tick that is mid-blip when the window ends is cut with the timer.
    // The tick is not alarm audio — it plays only while the alarm is
    // stood down — but the cycle-312 rule is that anything the owner can
    // start, the owner stops, and leaving a stray blip playing over a
    // resuming siren is exactly the confusion this cycle exists to avoid.
    safePause(treatingTickRef.current);
  }, []);

  useEffect(() => {
    if (!kioskUrgentRepeat || !treatingActive || !enabled) {
      clearTreatingTick();
      return;
    }
    const schedule = (delay: number) => {
      treatingTickTimerRef.current = window.setTimeout(() => {
        const el = treatingTickRef.current;
        if (el) {
          el.loop = false;
          el.volume = TREATING_TICK_VOLUME;
          try {
            el.currentTime = 0;
          } catch {
            /* some browsers throw before metadata loads — harmless */
          }
          safePlay(el);
        }
        schedule(TREATING_TICK_INTERVAL_MS);
      }, delay);
    };
    schedule(TREATING_TICK_FIRST_MS);
    return clearTreatingTick;
  }, [kioskUrgentRepeat, treatingActive, enabled, clearTreatingTick]);

  // Cycle 391: when a window ENDS and the cue comes back, it resumes at
  // the TIGHTENED end of the cycle-374 interval rather than back at 30 s.
  // Five to twenty minutes of declared treatment that did not work is new
  // information about severity, not a fresh episode — dropping the room
  // back to the most relaxed gap would be the alarm getting less
  // insistent as the situation got worse, which is backwards.
  //
  // Implemented by pinning the step counter at the floor on the FALLING
  // edge of the window, so the very next `startKioskUrgentRepeat` picks
  // the floor gap. The presence gate still applies on top: somebody
  // standing at the screen when it re-arms still holds the interval.

  const prevTreatingRef = useRef(false);
  useEffect(() => {
    const was = prevTreatingRef.current;
    prevTreatingRef.current = treatingActive;
    if (was && !treatingActive && kioskUrgentRepeat) {
      kioskRepeatCountRef.current = Math.ceil(
        (KIOSK_URGENT_REPEAT_MS - KIOSK_URGENT_REPEAT_FLOOR_MS) /
          KIOSK_URGENT_REPEAT_STEP_MS,
      );
    }
  }, [treatingActive, kioskUrgentRepeat]);

  // ─── Cycle 410.1: one soft chirp when the lead window opens ─────────
  // Cycle 405 treated a custody window producing zero cues as an
  // evaporated safety floor — and that is DECLARED custody, where a human
  // took responsibility. A lead window has nobody on the hook, so it must
  // not be the one silent surface. One chirp at open, the soft-tick asset
  // at its level, kiosk only, never on a descent (the low loop is already
  // sounding), never repeating. Same element and owner as the tick — and,
  // like the tick (the cycle-391 exemption), OUTSIDE `stopAllLoops`' falling-
  // edge cut: a ≤1 s one-shot that a Snooze landing inside it simply lets
  // finish. Louder, never quieter.
  const prevLeadRef = useRef<boolean>(episode?.audioLead ?? false);
  useEffect(() => {
    const was = prevLeadRef.current;
    const now = episode?.audioLead ?? false;
    prevLeadRef.current = now;
    if (!enabled) return;
    if (!leadOpenChirp(was, now, kioskUrgentRepeat, episode?.audible ?? false)) return;
    const el = treatingTickRef.current;
    if (!el) return;
    el.loop = false;
    el.volume = TREATING_TICK_VOLUME;
    try {
      el.currentTime = 0;
    } catch {
      /* some browsers throw before metadata loads — harmless */
    }
    safePlay(el);
  }, [episode?.audioLead, episode?.audible, kioskUrgentRepeat, enabled]);

  const startUrgentHighLoop = useCallback(() => {
    if (!enabledRef.current) return;
    // Cycle 242: urgent_high's own element (was highRef).
    const a = urgentHighRef.current;
    if (!a) return;
    a.loop = true;
    safePlay(a);
    setLoopActive(true);
  }, []);

  const playPredictionLowTwice = useCallback(() => {
    if (!enabledRef.current) return;
    const a = predictionRef.current;
    if (!a) return;
    // One re-arm via the `ended` event, then detach so subsequent
    // single fires don't double up.
    // Cycle 464.2 (astra round 2): the second play is owed only to THIS
    // request, and only if nothing cut the cue meanwhile. A cancel
    // (`safePause`, from alerts off, Snooze / Stop, call quiet, teardown)
    // releases the element's claim, so a late `ended`, or the next
    // request's own first `ended`, finds the token stale or the claim gone
    // and replays nothing.
    const token = (predictionSeqRef.current += 1);
    const genAtRequest = gateOf(a).gen;
    // This request supersedes the previous one, owed plays and all: its
    // follow-up goes before this one is armed (astra round 4).
    disarmPredictionFollowUp();
    const onEnded = () => {
      // A stale `ended` queued by an earlier play (cut at its very end, then
      // this request started) finds the element playing, not ended: it is
      // not this play's end, so the handler stays armed (astra round 3).
      if (!a.ended) return;
      // Only the armed follow-up is still attached (a newer request removed
      // the older one), so this is it; detach it either way.
      if (predictionFollowUpRef.current?.fn === onEnded) disarmPredictionFollowUp();
      else a.removeEventListener("ended", onEnded);
      if (predictionSeqRef.current !== token) return;
      if (!enabledRef.current) return;
      if (gateOf(a).gen !== genAtRequest) return; // cut by our own pause meanwhile
      safePlay(a);
    };
    predictionFollowUpRef.current = { el: a, fn: onEnded };
    a.addEventListener("ended", onEnded);
    safePlay(a);
  }, [disarmPredictionFollowUp]);

  const playNormal = useCallback(() => {
    if (!enabledRef.current) return;
    safePlay(normalRef.current);
  }, []);

  // Cycle 34.11: noData chime is silenced completely — the bridge
   // below + this imperative method are both no-ops. The ref + the
   // audio file are kept so a future cycle can re-enable cheaply.
  const playNoData = useCallback(() => {
    /* noData chime disabled — see cycle 34.11 */
  }, []);

  // Cycle 243.5: play the reminder chime once, then call `onEnded` so the
  // caller speaks the reminder TTS AFTER the chime (the requested sequence:
  // chime → spoken message). NOT gated by `alerts_enabled` — reminders are a
  // separate system from CGM alerts. `onEnded` fires on the audio `ended`
  // event, with a fallback timer so the TTS still speaks if the chime can't
  // play (autoplay-blocked / load error) — a reminder is never silent-and-mute.
  const playReminderChime = useCallback((onEnded?: () => void) => {
    const a = reminderRef.current;
    if (!a) {
      onEnded?.();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      a.removeEventListener("ended", finish);
      onEnded?.();
    };
    a.addEventListener("ended", finish);
    // Fallback: the chime is ~3.2s; speak by ~4.5s even if `ended` never
    // fires (e.g. play() rejected pre-unlock).
    window.setTimeout(finish, 4500);
    try {
      a.loop = false;
    } catch {
      /* ignore */
    }
    // Cycle 464.2: through the gate like every other sound (a chime that
    // starts inside the prime window waits for the restore instead of being
    // muted and paused by it); a blocked play still leaves the fallback
    // timer to trigger the TTS.
    safePlay(a);
  }, []);

  // ─── Cycle 242: Settings sound preview ───────────────────────────
  // Play a zone's currently-selected sound ONCE through the dedicated
  // preview element — never touches a real alarm element, generates no db
  // alert, opens no episode. A real urgent alarm always wins: preview is
  // blocked while a real urgent episode is sounding, and an effect below
  // cancels an in-flight preview the moment one starts.
  const [previewingZone, setPreviewingZone] = useState<AlarmZone | null>(null);

  const isUrgentSounding = (ep: AlarmEpisodeState | undefined): boolean =>
    ep != null &&
    ep.audioOn &&
    (ep.level === "urgent_low" || ep.level === "urgent_high");

  const previewSound = useCallback((zone: AlarmZone, id: string) => {
    if (isUrgentSounding(episodeRef.current)) return; // real alarm wins
    const el = previewRef.current;
    if (!el) return;
    const file = resolveAlarmSoundFile(zone, id);
    el.loop = false;
    if (!el.src.endsWith(file)) {
      el.src = file;
      el.load();
    }
    safePlay(el);
    setPreviewingZone(zone);
  }, []);

  const stopPreview = useCallback(() => {
    safePause(previewRef.current);
    setPreviewingZone(null);
  }, []);

  // Clear the previewing flag when the one-shot finishes on its own.
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const onEnded = () => setPreviewingZone(null);
    el.addEventListener("ended", onEnded);
    return () => el.removeEventListener("ended", onEnded);
  }, []);

  // A real urgent alarm cancels any in-flight preview.
  useEffect(() => {
    if (isUrgentSounding(episode)) {
      safePause(previewRef.current);
      setPreviewingZone(null);
    }
    // Only the urgent audioOn/level transition matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode?.audioOn, episode?.level]);

  // ─── Episode → audio bridge ──────────────────────────────────────
  // When the episode is active and !silenced, loop the right WAV for
  // the alert level. When the episode goes snoozed / silenced / idle,
  // stop *only* the loop the bridge previously started — that lets
  // TestAlarmDriver imperatively drive its own loops without the
  // bridge fighting it back to silence.
  const lastDrivenRef = useRef<"low" | "urgent_low" | null>(null);
  // Cycle 66: silenced wins over alarmActive.
  //
  // Cycle 63 also AND-gated targetLevel on `episode.alarmActive` to
  // pin the audio↔control invariant. But `alarmActive` is true
  // whenever the live reading sits in an alarming zone — independent
  // of whether the user pressed Stop. When the live reading stayed
  // low after Stop, `alarmActive` stayed true; without the cycle-66
  // shared-state silence path, a race in `audioOn` derivation could
  // leave the bridge restarting the loop (the "dead Stop" bug).
  //
  // Cycle 66 makes the source of truth explicit: only `audioOn`
  // gates the loop, and `audioOn = phase === "active" && !silenced`
  // (silenced is the cross-device shared state from `silenced_at`).
  // A silenced episode whose reading is still in-zone has
  // `alarmActive === true` but `audioOn === false` — the bridge
  // stops the loop and stays stopped until the engine re-arms by
  // clearing `silenced_at` (the same-side mild→urgent escalation).
  //
  // The on-screen Stop control is still rendered by the
  // `AlarmSilenceGuard` floor whenever `alarmActive` is true, so the
  // user can press Stop again at the urgent re-arm — same cycle-63
  // invariant (control visible whenever the alarm could be sounding)
  // just no longer coupled into the audio path.
  // Current sustained zone + freshness, recomputed off the ticking clock
  // (`nowMs`, 30s) so staleness re-evaluates without a new frame. Defined
  // here (above the bridge) because the cycle-231 loop gate needs
  // `readingFresh`; the cycle-216 cadence effects below reuse the same
  // `curZone` / `readingFresh`.
  const latestReading = socket.lastReading;
  const curZone: Zone | null = latestReading
    ? zoneOf(latestReading.sgv, thresholds)
    : null;
  const readingFresh =
    latestReading != null &&
    !isStale(latestReading.timestamp, nowMs, latestReading.provider);

  // Cycle 231: arm the continuous loop ONLY for a genuinely-live alarm
  // belonging to the ACTIVE member. Two gates beyond `episode.audioOn`:
  //
  //   (1) `activeMemberId != null` — kills the connect-time multi-alarm
  //       burst. On (re)connect, before the active member resolves, the
  //       snapshot seeds top-level `lastReading` from a FALLBACK member
  //       (any member with a reading); if that member is alarming the
  //       episode/audio would briefly sound for someone who isn't the
  //       active member. Holding the loop until the active member is known
  //       means the seed converges banner/episode STATE silently with no
  //       audio for non-active members.
  //   (2) `readingFresh` — a stale seed (dead sensor) never sounds (the
  //       "stale glucose → neutral" invariant on the audio side).
  //
  // A device reconnecting mid-real-episode still sounds: the active member
  // is known and its fresh, still-alarming reading rides the snapshot, so
  // the loop arms ONCE — preserving real mid-episode audio (deliverable 6).
  // The one-shot `high` chime + the prediction sound are already gated to
  // genuine live `alert`/`prediction` events via the cycle-112 provenance
  // split (`lastAlertEvent` / `lastPredictionEvent`, never set by a seed).
  const audioArmable =
    activeMemberId != null && readingFresh;
  // Cycle 232: the continuous loop is now LOW-side only. `urgent_high` no
  // longer has a continuous audible loop — highs (high + urgent_high) use
  // the sustained 1h/2h/hourly chime cadence below, matching the
  // server-side push demotion. `low` was never a continuous loop here
  // (the cycle-15 episode bridge only ever looped urgent_low; `low`'s
  // single chime is its own thing) — the only removal is `urgent_high`.
  //
  // ⚠ CYCLE 403 — `!callQuiet` IS A TERM HERE, AND ONLY HERE, FOR THE
  // LOW-SIDE LOOP. While we are on the phone with THIS session's own
  // account, its in-app alarm audio is cut: a caregiver who picked up
  // cannot hear what the call is saying over an identical siren coming
  // out of the same handset. That is the entire defect this cycle exists
  // to fix, and it is why the term sits in the one derivation every
  // low-side sound reads rather than being sprinkled at call sites.
  //
  // Everything that makes this safe is elsewhere and is not weakened by
  // it: the quiet is bounded by a SERVER ceiling written into the frame's
  // `until` (a lost end-frame still ends in loud), it applies to exactly
  // one account, `callQuietActive` short-circuits on the kiosk so the
  // never-silent floor is untouched, and NOTHING here changes the
  // DISPLAY — every banner, hero and strip stays exactly where it is.
  //
  // ⚠ CYCLE 410 — `episode.audibleLevel`, NOT `episode.level`. The lead
  // window defers the URGENT-LOW audio upgrade while the phone calls run:
  // under a window on the bound urgent row the audible level is the open,
  // un-quieted `low` sibling's (a descent — the low loop simply continues)
  // or null (a cold crossing — nothing sounds until the deadline on this
  // device's own clock). With no window in play `audibleLevel === level`,
  // so every existing path below is byte-identical. The window is AUDIO
  // ONLY: `audioOn` — the cycle-63/81 audio↔control gate — is untouched,
  // and the level change here is what lets the bridge stop the urgent cue
  // (`lastDrivenRef !== targetLevel` → `stopAllLoops`) and restart the
  // right loop the instant the window ends. Deferred, never removed.
  // ⚠ CYCLE 451.1 — THIS DERIVATION MOVED TO `lib/alarmAudible.ts`, AND IS
  // NOW SHARED WITH THE ROUTE-RETURN RULE. The terms and their truth are
  // unchanged: a missing `episode` yields `audioOn: false` and a null
  // `audibleLevel`, which is exactly what the leading `episode &&` did. It is
  // shared so that "alarm audio is sounding" has ONE derivation — the rule
  // that takes the app back to `/now` reads this same function, so it cannot
  // drift away from what actually sounds. ⚠ The PREVIEW term lives on the
  // route side only (`alarmAudioSounding`): a Settings test alarm must never
  // be able to suppress a real loop.
  const targetLevel: "low" | "urgent_low" | null = audibleAlarmLevel({
    enabled,
    audioArmable,
    // Written as explicit member reads, not optional chaining: the
    // cycle-410 source guard in `audioLeadScreen.test.ts` pins that THIS
    // expression names `episode.audibleLevel` and `episode.audioOn`, and
    // that the raw episode level is absent from it. That property is the
    // point of the lead window and is unchanged here, so the guard is left
    // exactly as it was — and it reads RAW source, so this note may not
    // spell the forbidden token either.
    audioOn: episode ? episode.audioOn : false,
    callQuiet,
    audibleLevel: episode ? episode.audibleLevel : null,
  });

  // ─── Cycle 312 (operator directive, the operator 2026-07-07): "whenever I hit
  // snooze, all sound needs to stop immediately, for alarms." ──────────
  // The loop-driver below already stops the CONTINUOUS low-side loop the
  // moment `audioOn` falls (snooze flips phase → "snoozed" optimistically
  // on the pressing device; other devices follow the `alert_snoozed`
  // frame). What it never touched was a ONE-SHOT chime already mid-play —
  // the high / urgent-high crossing chime, the hourly high-cadence chime,
  // the low single chime, and the prediction sound all played out their
  // remaining seconds after Snooze (and on a high-side episode, Snooze
  // never touched the elements at all). This effect hard-cuts ALL alarm
  // audio elements on the FALLING edge of `audioOn` — which covers
  // Snooze AND Stop, on every device. Deliberately NOT paused here:
  // `normalRef` (the in-range/recovery cue can legitimately start on the
  // same falling edge — cycle 304), `noDataRef` (the sensor-outage alert
  // is independent of the glucose episode being snoozed), and
  // `reminderRef` (reminders are not alarms). Stopping-only on an
  // explicit user action / episode end — this can never START audio, so
  // the cycle-63/81 audio↔control invariant is untouched.
  const prevAudioOnRef = useRef<boolean>(episode?.audioOn ?? false);
  useEffect(() => {
    const cur = episode?.audioOn ?? false;
    const was = prevAudioOnRef.current;
    prevAudioOnRef.current = cur;
    if (was && !cur) {
      stopAllLoops(); // the four alarm-level elements (loops + one-shots)
      safePause(predictionRef.current);
    }
  }, [episode?.audioOn, stopAllLoops]);

  // ─── Cycle 403: the same hard cut, on the RISING edge of call quiet ──
  // `targetLevel` going null already stops the low-side LOOP, but — for
  // exactly the reason cycle 312 exists — it never touched a ONE-SHOT
  // already mid-play: the high / urgent-high crossing chime, the hourly
  // high-cadence chime, the low single chime and the prediction sound
  // would all play out their remaining seconds into the caregiver's ear
  // while they are trying to hear the call. This reuses the cycle-312
  // pair verbatim rather than restating it, so a future alarm element
  // added to `stopAllLoops` joins BOTH cuts automatically (the standing
  // rule: any new alarm sound element must join the cut).
  //
  // Deliberately not paused, matching cycle 312: `normalRef` (the
  // return-to-range cue is not an alarm), `noDataRef` (a sensor outage is
  // independent of any call) and `reminderRef` (reminders are not
  // alarms). Stopping-only — this can never START audio, so the
  // cycle-63/81 audio-to-control invariant is untouched, and it cannot
  // fire on the kiosk at all because `callQuiet` is false there.
  const prevCallQuietRef = useRef<boolean>(false);
  useEffect(() => {
    const was = prevCallQuietRef.current;
    prevCallQuietRef.current = callQuiet;
    if (!was && callQuiet) {
      stopAllLoops();
      safePause(predictionRef.current);
    }
  }, [callQuiet, stopAllLoops]);

  // One-shot retry of whatever we're driving, the first time
  // audioUnlocked flips true. Catches the case where the alarm fired
  // before the user tapped (initial play() was blocked; the elements
  // sat paused even after unlock).
  const retriedAfterUnlockRef = useRef(false);

  useEffect(() => {
    const sameLevel = lastDrivenRef.current === targetLevel;
    // Cycle 464.2 (astra): the retry exists for a cue the autoplay policy
    // BLOCKED before the tap, so it runs only when the driven element is
    // paused; a loop or repeat that is already sounding is left alone (the
    // retry restarted it from the top on every first tap).
    const drivenEl = targetLevel === "low" ? lowRef.current : urgentLowRef.current;
    const retryOwed = audioUnlocked && !retriedAfterUnlockRef.current && targetLevel != null;
    // A kiosk repeat whose timer is scheduled is RUNNING, paused only
    // because it is between two cues; a retry would clear that timer and
    // reset the escalation (astra round 2). Nothing to unlock there either.
    const repeatRunning = targetLevel === "urgent_low" && kioskRepeatTimerRef.current != null;
    if (retryOwed && ((drivenEl != null && !drivenEl.paused) || repeatRunning)) {
      // Sounding already, or a running repeat: there was nothing to unlock.
      // Consumed, so it cannot fire at a later gap either.
      retriedAfterUnlockRef.current = true;
    }
    const needsUnlockRetry = retryOwed && !repeatRunning && (drivenEl == null || drivenEl.paused);
    if (sameLevel && !needsUnlockRetry) return;
    if (needsUnlockRetry) retriedAfterUnlockRef.current = true;

    if (lastDrivenRef.current != null && lastDrivenRef.current !== targetLevel) {
      stopAllLoops();
    }
    if (targetLevel === "low") startLowLoop();
    else if (targetLevel === "urgent_low") {
      // ── Cycle 374: urgent low no longer loops ──────────────────────
      // KIOSK → the escalating repeating cue (the pinned floor: the one
      // surface in the room, and the one with no push/call behind it).
      // Everywhere else → play ONCE per fire; persistence there is
      // carried by the push cadence and the cycle-371/373 call sequence.
      // `low` is untouched, and so are `high`/`urgent_high`.
      if (urgentLowPlaybackMode(kioskUrgentRepeat) === "kiosk-repeat") {
        startKioskUrgentRepeat();
      } else if (
        !inAppCueQuiet(Date.now(), voiceCallQuietUntil, kioskUrgentRepeat)
      ) {
        playUrgentLowOnce();
        // Cycle 451.1: the cue really played, so the app owes the caregiver
        // a control. The route rule takes them to `/now` ONCE on this edge
        // and does not pin — a ~3 s one-shot is not sustained audio.
        setUrgentCueSeq((n) => n + 1);
      }
      // Cycle 381: suppressed ⇒ we still fall through to mark the level as
      // driven. The cue is ONE-SHOT PER FIRE, so skipping it must mean
      // skipped, not deferred — re-firing it the moment the window lapses
      // would put the sound back on top of the call it was avoiding.
      //
      // `low` is deliberately NOT suppressed: it plays a continuous LOOP,
      // and silencing a continuous alarm for 45s is a materially bigger
      // call than skipping one 8.2s one-shot. Narrow on purpose.
    }
    lastDrivenRef.current = targetLevel;
  }, [
    targetLevel,
    audioUnlocked,
    stopAllLoops,
    startLowLoop,
    // Constant for the life of a surface in practice, but declared so a
    // future variant switch can never leave the bridge driving the wrong
    // urgent-low playback.
    kioskUrgentRepeat,
    startKioskUrgentRepeat,
    playUrgentLowOnce,
    voiceCallQuietUntil,
  ]);

  // ─── One-shot reactions to LIVE alert events ─────────────────────
  // Cycle 112: the `high` one-shot fires off `socket.lastAlertEvent` —
  // emitted ONLY by a live `alert` frame for the active member — not off
  // the cached `socket.lastAlert`, which the cycle-61 snapshot seeds on
  // every (re)connect. Before this, a deploy-reload (cycle-33
  // `useAppVersionPoller` force-reload) wiped this hook's "already
  // played" ref, then the snapshot re-seeded a stale unacked `high` into
  // `lastAlert` and the freshly mounted effect replayed `high.wav` — the
  // operator heard a high alarm on every deploy. Keying off an explicit
  // live-fire event with a monotonic `seq` closes that: a seed/reseed
  // never sets the event field, so it never fires. The cycle-66
  // mild→urgent re-arm rebroadcasts a live frame → new `seq` → re-fires.
  // Low / urgent_* still go through the episode bridge above.
  const lastHandledAlertSeqRef = useRef<number | null>(null);

  useEffect(() => {
    const ev = socket.lastAlertEvent;
    if (!ev) return;
    if (ev.seq === lastHandledAlertSeqRef.current) return;
    lastHandledAlertSeqRef.current = ev.seq;
    const a = ev.alert;
    if (a.acknowledged_at != null || a.fired_at == null) return;
    if (!enabledRef.current) return;
    // Cycle 429: the shared event age guard — a crossing older than the
    // bound is history, not an event, whatever minted it. (A backlog mints
    // no event at all; this is the belt for anything that slips through.)
    if (!cueEventFresh(a.fired_at ?? a.triggered_at, Date.now())) return;
    // Cycle 240: RESTORE the high-family entry chime. The point of the
    // alarm is that a high SOUNDS the moment the user crosses into it
    // (cycle 232 had removed this, leaving the first audible high chime an
    // hour out — an unacceptable silent crossing). A live `high` OR
    // `urgent_high` crossing now plays `high.wav` ONCE immediately; the
    // sustained cadence below then replays it every hour while the high
    // persists. This is a one-shot on a LIVE event (cycle-112 seq guard +
    // staleness guard above), so a deploy/reload reseed never replays it,
    // and the cycle-66 mild→urgent re-arm (a fresh live frame → new seq)
    // re-chimes on the worsening crossing. Low / urgent_low stay loop-
    // driven by the episode bridge above and are untouched.
    // Cycle 242: play the zone's OWN element so high vs urgent_high carry
    // their distinct custom sounds (urgent_high no longer shares highRef).
    if (a.level === "high") {
      playHigh();
    } else if (a.level === "urgent_high") {
      playUrgentHigh();
    }
  }, [socket.lastAlertEvent, playHigh, playUrgentHigh]);

  // Predictions: fire the prediction sound when a live `prediction`
  // frame arrives for the active member (cycle 112: keyed off
  // `lastPredictionEvent`, the same event-vs-cache split as alerts, so
  // a reconnect/reload can't replay it). The existing horizon /
  // unresolved / level gating is unchanged.
  const lastHandledPredictionSeqRef = useRef<number | null>(null);

  useEffect(() => {
    const ev = socket.lastPredictionEvent;
    if (!ev) return;
    if (ev.seq === lastHandledPredictionSeqRef.current) return;
    lastHandledPredictionSeqRef.current = ev.seq;
    const p = ev.prediction;
    if (p.outcome != null) return;
    // Cycle 429: the shared event age guard — on the server's FIRE time.
    // ⚠ NOT `predicted_at`: that is the SENSOR timestamp of the reading the
    // prediction was fitted on, routinely one to five minutes old by the
    // time the frame lands (Share lag + the poll cycle). Gating on it
    // would have silenced the predicted-low cue for good (safety review,
    // blocking finding 1). `fired_at` is stamped when the row is minted.
    if (!cueEventFresh(p.fired_at ?? p.predicted_at, Date.now())) return;
    const horizonEnd =
      new Date(p.predicted_at).getTime() + p.horizon_minutes * 60_000;
    if (horizonEnd <= Date.now()) return;
    if (
      p.predicted_level !== "predicted_low" &&
      p.predicted_level !== "predicted_urgent_low"
    ) {
      return;
    }
    if (!enabledRef.current) return;
    playPredictionLowTwice();
  }, [socket.lastPredictionEvent, playPredictionLowTwice]);

  // Active-member changes already clear top-level state in the socket
  // reducer; stop any in-flight loop so a member switch doesn't leave
  // audio playing for the previous member. Also reset the return-to-
  // range tracker so a switch from an alarming member to an in-range
  // one doesn't fire the chime. (Cycle 34.11: the previous noData
  // tracker reset is dropped along with the noData bridge.)
  useEffect(() => {
    stopAllLoops();
    prevZoneRef.current = null;
  }, [activeMemberId, stopAllLoops]);

  // ─── Cycle 33: return-to-range bridge ────────────────────────────
  // Plays `normal.wav` once when the latest reading transitions from
  // any out-of-range zone back into `in_range`. Gated on `enabled`
  // and on the episode being idle so the chime doesn't stomp an
  // active alarm loop — the episode's own recovery rule (rising AND
  // back above target_low, falling AND back below target_high) ends
  // the episode, at which point any subsequent in-range reading will
  // *also* satisfy the bridge if the previous one was still out of
  // range. We treat `unknown` and `in_range` as non-triggering
  // previous zones to avoid firing on first connection.
  const prevZoneRef = useRef<Zone | null>(null);
  useEffect(() => {
    const latest = socket.lastReading;
    if (!enabled || !latest) {
      prevZoneRef.current = latest ? zoneOf(latest.sgv, thresholds) : null;
      return;
    }
    const curZone = zoneOf(latest.sgv, thresholds);
    const prevZone = prevZoneRef.current;
    prevZoneRef.current = curZone;
    if (prevZone == null) return;
    if (curZone !== "in_range") return;
    if (
      prevZone !== "low" &&
      prevZone !== "urgent_low" &&
      prevZone !== "high" &&
      prevZone !== "urgent_high"
    ) {
      return;
    }
    // Don't stomp an active alarm loop. The episode bridge keeps the
    // alarm WAV playing through a brief in-range blip; its own
    // recovery rule will end the episode, after which a subsequent
    // in-range reading triggers this branch cleanly.
    if (episode && episode.phase !== "idle") return;
    // Cycle 429: the edge must have arrived LIVE — the last delivery not
    // a backlog, and within the event age bound of now. A zone walk
    // replayed from a thawed socket is history, not a return to range.
    const meta = socket.lastFrameMeta;
    if (meta == null || meta.catchUp) return;
    if (Date.now() - meta.receivedAtMs > CUE_EVENT_AGE_MS) return;
    safePlay(normalRef.current);
  }, [socket.lastReading, socket.lastFrameMeta, enabled, episode, thresholds]);

  // ─── Cycle 243: in-range cue on episode RECOVERY ─────────────────
  // The cycle-33 reading-edge bridge above is gated on `episode.phase
  // === "idle"`, but cycle-82 made recovery server-authored with a ~90s
  // in-range dwell: at the alarming→in_range reading the episode is still
  // `active` (chime suppressed), and by the time it recovers `prevZoneRef`
  // has already advanced to `in_range` (the edge is lost). Net: the
  // in-range/recovery cue never fired after a real alarm. Fix: ALSO play
  // it when the episode transitions non-idle → idle (recovered/cleared)
  // while the latest reading is fresh + in range — the true "alarm
  // cleared, back in range" moment. No double-fire: in the episode flow
  // the reading-edge bridge is gated off at the transition; in the
  // no-episode flow (TestAlarmDriver) `episode` is undefined so this never
  // runs. The reading-edge bridge stays as the no-episode path.
  const prevPhaseRef = useRef<EpisodePhase | null>(null);
  // Cycle 428: the cycle-304/398 "early in-range cue already played" flag
  // is gone with the early stand-down — the display clears ONLY on the
  // server's recovery frame, so this formal-recovery trigger (phase →
  // idle) is the ONE owner of the in-range cue: once per episode, at
  // recovery, on every device together.
  useEffect(() => {
    const phase = episode?.phase ?? null;
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (prev == null || phase == null) return;
    if (!(prev !== "idle" && phase === "idle")) return; // recovered/cleared
    if (!enabledRef.current) return;
    // Cycle 429: the recovery cue plays only for a FRESH recovery — the
    // bound row's own `recovered_at` within the event age bound, arriving
    // live (never from a backlog or a seed). A recovery replayed hours
    // late, or a latch cleared by the server-truth poll, is history.
    if (socket.lastFrameMeta?.catchUp) return;
    if (!cueEventFresh(socket.lastAlert?.recovered_at ?? null, Date.now())) return;
    const latest = socket.lastReading;
    if (!latest) return;
    if (isStale(latest.timestamp, Date.now(), latest.provider)) return;
    if (zoneOf(latest.sgv, thresholds) !== "in_range") return;
    safePlay(normalRef.current);
    // enabledRef is a ref; the recovery edge is what matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode?.phase, socket.lastReading, thresholds]);

  // ─── Cycle 216: recurring zone cadences ──────────────────────────
  // Two state-driven intervals modeled on the cycle-33 no-data pattern
  // (a setInterval keyed off the CURRENT sustained zone, cleared the
  // moment the condition ends). The interval id lives in a ref so the
  // timer PERSISTS across reading updates — the effect re-runs on every
  // frame/tick but only (re)creates the interval on the not-armed →
  // armed transition, so the first audible re-fire is exactly one full
  // interval after arming (never on arm, never on a snapshot seed).

  // (`latestReading` / `curZone` / `readingFresh` are defined above the
  // episode→audio bridge — the cycle-231 loop gate needs `readingFresh`.)

  // (1) High — the cycle-232 sustained cadence. Replay `high.wav` (the
  // normal, non-critical sound) every 60 min while sustained HIGH-SIDE
  // (`high` OR `urgent_high` — urgent_high no longer loops; it shares this
  // cadence), so the first chime is one interval (1 h) after arming, then
  // hourly — never on crossing. Gated on the same source of truth the
  // loops use — `episode.audioOn` (= phase === "active" && !silenced,
  // which also goes false while snoozed) — so Stop (available on
  // high/urgent_high per Cycle 73) halts the cadence and Snooze pauses it
  // (re-arms one interval later when the window passes). Cleared on
  // leaving the high family (recovery to a non-high zone), stale/no-data,
  // or disabled. The interval id lives in a ref so it PERSISTS across
  // reading updates (it's only (re)created on the not-armed→armed
  // transition) — so worsening high→urgent_high does NOT restart the
  // clock, matching the server anchor.
  const highChimeIntervalRef = useRef<number | null>(null);
  const highChimeArmed = mayEmitHighCadenceCue({
    enabled,
    readingFresh,
    zone: curZone,
    episodeAudioOn: episode != null ? episode.audioOn : null,
  });
  // Cycle 351 (SAFETY): the SAME predicate, RECOMPUTED FROM SCRATCH at
  // FIRE time. The interval callback used to check only `enabledRef`,
  // trusting the state it was armed on — but iOS WKWebView / Android
  // WebView suspend JS timers while backgrounded and fire the overdue
  // callback on foreground (the cycle-309 webview behavior). A cadence
  // armed during a real high could therefore chime at app-open against a
  // now-fresh, in-range reading: the 2026-07-19 05:53 false high.
  //
  // Deliberately NOT a snapshot of the render-time `highChimeArmed`: a ref
  // written during render can be no fresher than the last COMMITTED render,
  // and on a webview resume the flushed timer callback can run before any
  // re-render — so a snapshot would still hold the stale pre-background
  // value and chime anyway. Reading the raw inputs and re-deriving here
  // (with `Date.now()` evaluated at fire time) is immune to render timing.
  //
  // This is the client-side twin of the cycle-346/349 server rule — a
  // repeating alarm rail must assert the member's situation RIGHT NOW,
  // never the one captured when it armed. A still-live high satisfies this
  // exactly as it did at arm time, so a real high is never muted.
  const latestReadingRef = useRef(latestReading);
  latestReadingRef.current = latestReading;
  const thresholdsRef = useRef(thresholds);
  thresholdsRef.current = thresholds;
  const mayChimeHighNow = useCallback((): boolean => {
    const r = latestReadingRef.current;
    const now = Date.now();
    return mayEmitHighCadenceCue({
      enabled: enabledRef.current,
      readingFresh: r != null && !isStale(r.timestamp, now, r.provider),
      zone: r != null ? zoneOf(r.sgv, thresholdsRef.current) : null,
      episodeAudioOn: episodeRef.current?.audioOn ?? null,
    });
  }, []);
  // Cycle 242: chime the element matching the CURRENT high-side zone at fire
  // time (high → highRef, urgent_high → urgentHighRef) so each plays its own
  // selected sound. A ref so the long-lived interval reads the latest zone.
  const highSideElRef = useRef<HTMLAudioElement | null>(null);
  highSideElRef.current =
    curZone === "urgent_high" ? urgentHighRef.current : highRef.current;
  useEffect(() => {
    if (highChimeArmed && highChimeIntervalRef.current == null) {
      highChimeIntervalRef.current = window.setInterval(() => {
        // Cycle 351: re-gate at FIRE time (see `mayChimeHighNow` above).
        // `enabledRef` alone let a suspended-timer catch-up fire sound a
        // high against a recovered / in-range / stale reading.
        if (mayChimeHighNow()) safePlay(highSideElRef.current);
      }, HIGH_CADENCE_INTERVAL_MS);
    } else if (!highChimeArmed && highChimeIntervalRef.current != null) {
      window.clearInterval(highChimeIntervalRef.current);
      highChimeIntervalRef.current = null;
    }
  }, [highChimeArmed]);

  // (2) In range — REMOVED (cycle 235.3, operator request). The recurring
  // in-range chime (`normal.wav` every 33 min while simply sitting in range,
  // regardless of any preceding alarm) is gone. An in-range sound now plays
  // ONLY when the reading RETURNS to range from a high or low — that's the
  // cycle-33 return-to-range transition bridge above (prev zone ∈ {low,
  // urgent_low, high, urgent_high} → in_range), which is unchanged. No more
  // unprompted chimes from a device that's just sitting in range.

  // Clear the high cadence interval on unmount and on active-member change
  // (the member switch already resets prevZoneRef + stops loops above).
  useEffect(() => {
    return () => {
      if (highChimeIntervalRef.current != null) {
        window.clearInterval(highChimeIntervalRef.current);
        highChimeIntervalRef.current = null;
      }
    };
  }, [activeMemberId]);

  // ─── Cycle 360.5 / 386: kiosk in-app no-data alert ───────────────
  // The cycle-34.11-disabled no-data chime is REPLACED by a state-driven
  // alert, KIOSK ONLY. The always-foregrounded Android tablet can't rely on
  // push for a dark-feed alert (Android FCM plays a generic channel sound and
  // a wall kiosk may never surface it), so it sounds the distinct `noData`
  // tone in the WebView when the feed goes dark. Phones/web get the cycle-360
  // critical push instead. `playNoData` stays a no-op (the old imperative
  // one-shot has no caller); this is driven off `noDataAlarmActive` below.
  //
  // Cycle 386: the tone plays ONCE per outage — it no longer loops. The
  // BANNER is what persists now (`noDataAlarmActive` is unchanged and stays
  // true for the whole outage), so the dark feed is still visible on every
  // screen; only the repeated audio is gone.
  //
  // Outage-keyed Silence: `silenceNoData()` records the CURRENT last-reading
  // timestamp. The alert stays cleared while the newest reading is still that
  // same one (the same outage). Data returning advances the timestamp (the
  // gate is false anyway once fresh), and a LATER outage carries a different
  // newest reading → it re-alarms. So a known outage can be dismissed, but a
  // NEW one always sounds — the no-data alert can never be permanently muted
  // (the safety rule for an un-silenceable alarm).
  //
  //
  // Cycle 453.2: the Silence is keyed by the OUTAGE'S newest reading row id
  // (`lib/noDataAlarm.ts`), which belongs to exactly one member — silencing
  // one member's outage can never mute another's.
  const [noDataSilences, setNoDataSilences] = useState<NoDataSilences>([]);
  const silenceNoData = useCallback(() => {
    setNoDataSilences((prev) => withNoDataSilence(prev, noDataOutageKey(latestReadingRef.current)));
  }, []);

  const noDataReadingAgeMs =
    latestReading != null
      ? nowMs - new Date(latestReading.timestamp).getTime()
      : null;
  // Gates (ALL required): kiosk loop enabled, alarms enabled, an active
  // member is resolved, there IS a last reading (a feed that WAS working, now
  // dark — never a brand-new account with no data), the feed is dark ≥ 15 min
  // (longer than the ~10-min hero staleness → no false alarm on a brief gap),
  // NO live glucose alarm (a real low/high always wins the audio), NO active
  // sensor-pause hold (cycle 354 — planned outage), and not silenced for
  // THIS outage.
  const noDataAlarmActive =
    noDataLoopEnabled &&
    enabled &&
    activeMemberId != null &&
    latestReading != null &&
    noDataReadingAgeMs != null &&
    noDataReadingAgeMs >= NO_DATA_ALARM_MS &&
    !(episode?.isLive ?? false) &&
    !sensorPauseActive &&
    // Cycle 468: a locked member's quiet feed is the lock, not an outage.
    !serviceLocked &&
    !noDataSilenced(noDataSilences, noDataOutageKey(latestReading));

  // Cycle 453.2: the outage this screen is alarming for (its newest reading
  // row id). Null when no outage alarm is up.
  const noDataActiveKey = noDataAlarmActive ? noDataOutageKey(latestReading) : null;

  // Cycle 386: the no-data tone plays ONCE per outage — it no longer loops
  // (operator direction 2026-08-12). A continuous loop on a wall kiosk is
  // a room-filling drone for a condition that is not itself a glucose
  // emergency, and it made the Silence control effectively mandatory.
  //
  // WHAT CARRIES THE PERSISTENCE INSTEAD, and why this is not a silent
  // failure: `noDataAlarmActive` is unchanged, so the `NoDataAlarmBanner`
  // stays on screen for the whole outage — the surface a glance actually
  // reads — and the cycle-360 CRITICAL push still fires to every phone in
  // the audience. What is gone is only the repeated sound.
  //
  // Fire on the not-active → active edge, stop on active → not-active.
  // A one-shot ends paused, so `el.paused` can no longer distinguish
  // "finished" from "blocked" — hence `safePlayReporting`: a rejected
  // play() releases the latch so the `audioUnlocked` dep retries it once
  // audio unlocks, while a successful one never replays. `safePause` on
  // the falling edge clears `loop` + resets `currentTime`.
  //
  // Cycle 453.2: the latch holds WHICH outage the tone was played for, not
  // a bare flag. A switch from one dark member to another is a new key, so
  // the second member's tone plays without depending on an intermediate
  // render in which no outage is up.
  const noDataDrivenRef = useRef<string | null>(null);
  useEffect(() => {
    const el = noDataRef.current;
    if (!el) return;
    if (noDataActiveKey != null) {
      if (noDataCueDue(noDataActiveKey, noDataDrivenRef.current)) {
        if (noDataDrivenRef.current != null) safePause(el);
        el.loop = false;
        const key = noDataActiveKey;
        noDataDrivenRef.current = key;
        void safePlayReporting(el).then((started) => {
          if (!started && noDataDrivenRef.current === key) {
            noDataDrivenRef.current = null;
          }
        });
      }
    } else if (noDataDrivenRef.current != null) {
      safePause(el);
      noDataDrivenRef.current = null;
    }
  }, [noDataActiveKey, audioUnlocked]);

  // If alerts_enabled flips off mid-loop, stop the loop. Re-enabling
  // does NOT replay anything that was missed.
  useEffect(() => {
    if (!enabled) {
      stopAllLoops();
      // Cycle 464.2 (astra): the predicted-low and return-to-range cues
      // too, as the audioOn falling edge already does for the prediction; a
      // play queued behind the unlock prime would otherwise still run after
      // alerts were switched off.
      safePause(predictionRef.current);
      safePause(normalRef.current);
    }
  }, [enabled, stopAllLoops]);

  // Final teardown.
  useEffect(() => {
    return () => {
      safePause(lowRef.current);
      safePause(highRef.current);
      safePause(urgentLowRef.current);
      safePause(urgentHighRef.current);
      safePause(predictionRef.current);
      safePause(normalRef.current);
      safePause(noDataRef.current);
      safePause(previewRef.current);
      safePause(reminderRef.current);
      // Cycle 464.2 (astra): the treating tick / lead chirp element too, so
      // nothing playing or queued behind the prime survives the unmount; the
      // kiosk repeat's timer, so no cue is scheduled on an unmounted hook;
      // and the prediction cue's armed follow-up, so no listener outlives it.
      safePause(treatingTickRef.current);
      clearKioskUrgentRepeat();
      disarmPredictionFollowUp();
    };
  }, []);

  // Cycle 407: the check-in cue — the SAME element and gain as the
  // five-minute treating tick, fired once on the arrival of a question.
  // Kiosk-only (the tick's own gate): the phone's check-in is spoken by
  // the app's TTS and needs no cue over the acceptor's ear.
  const playTreatingTick = useCallback(() => {
    if (!kioskUrgentRepeat || !enabled) return;
    const el = treatingTickRef.current;
    if (!el) return;
    el.loop = false;
    el.volume = TREATING_TICK_VOLUME;
    try {
      el.currentTime = 0;
    } catch {
      /* harmless before metadata */
    }
    safePlay(el);
  }, [kioskUrgentRepeat, enabled]);
  const lastCheckinCueSeqRef = useRef<number | null>(null);
  const checkinSeq = opts.socket.lastCheckinEvent?.seq ?? null;
  useEffect(() => {
    if (checkinSeq == null || lastCheckinCueSeqRef.current === checkinSeq) {
      return;
    }
    // Record the seq only when we actually cue — a frame that lands during
    // a transient `treatingActive === false` must not be swallowed.
    if (!treatingActive) return;
    lastCheckinCueSeqRef.current = checkinSeq;
    playTreatingTick();
  }, [checkinSeq, treatingActive, playTreatingTick]);

  return useMemo(
    () => ({
      playLow,
      playHigh,
      startUrgentLowLoop,
      playUrgentLowOnce,
      startUrgentHighLoop,
      startLowLoop,
      stopAllLoops,
      playPredictionLowTwice,
      playNormal,
      playNoData,
      playTreatingTick,
      playReminderChime,
      loopActive,
      audibleAlarmLevel: targetLevel,
      kioskUrgentRepeat,
      urgentCueSeq,
      previewSound,
      stopPreview,
      previewingZone,
      noDataAlarmActive,
      silenceNoData,
    }),
    [
      playLow,
      playHigh,
      startUrgentLowLoop,
      playUrgentLowOnce,
      startUrgentHighLoop,
      startLowLoop,
      stopAllLoops,
      playPredictionLowTwice,
      playNormal,
      playNoData,
      playTreatingTick,
      playReminderChime,
      loopActive,
      targetLevel,
      kioskUrgentRepeat,
      urgentCueSeq,
      previewSound,
      stopPreview,
      previewingZone,
      noDataAlarmActive,
      silenceNoData,
    ],
  );
}
